import { useEffect, useState, useMemo, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { useTenant } from '../../hooks/useTenant';
import { useAuth } from '../../hooks/useAuth';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { useRealTimeMetrics } from '../../hooks/useRealTimeMetrics';
import { useDateRange } from '../../hooks/useDateRange';
import { Download } from 'lucide-react';

export default function AnalyticsView() {
  const { user } = useAuth();
  const { selectedTenantId } = useTenant();
  const { days, label: dateLabel } = useDateRange();
  const [analytics, setAnalytics] = useState<any>(null);

  const { data, isLoading } = useRealTimeMetrics({
    endpoint: 'analytics',
    interval: 30000,
    days,
  });

  useEffect(() => {
    if (data) {
      setAnalytics(data.analytics || data);
    }
  }, [data]);

  const handleExport = useCallback(() => {
    const tenantId = user?.role === 'superadmin' ? selectedTenantId : user?.tenantId;
    if (!tenantId) return;
    const token = localStorage.getItem('access_token');
    const url = `/api/dashboard/export/analytics?tenantId=${tenantId}&days=${days}`;
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.blob())
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = `analytics-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(objectUrl);
      });
  }, [user, selectedTenantId, days]);

  if (user?.role === 'superadmin' && !selectedTenantId) {
    return (
      <div className="p-6 bg-red-50 rounded-xl border border-red-300 text-red-900">
        Please select a tenant from the dropdown above to view data.
      </div>
    );
  }

  const COLORS = useMemo(() => ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'], []);

  // Memoize event type data processing
  const eventTypeData = useMemo(() => {
    if (!analytics?.eventsByType) return [];
    return Object.entries(analytics.eventsByType)
      .map(([type, events]: [string, any]) => {
        const value = typeof events === 'number'
          ? events
          : Array.isArray(events)
            ? events.length
            : 0;

        return {
          name: type || 'unknown',
          value,
        };
      })
      .filter((entry) => entry.value > 0);
  }, [analytics?.eventsByType]);

  // Memoize session data processing
  const sessionData = useMemo(() => {
    if (!analytics?.sessionsByDay) return [];
    if (days <= 1) {
      // Hourly view: keys are "YYYY-MM-DDTHH", fill all 24 hours
      const hourMap: Record<string, number> = {};
      Object.entries(analytics.sessionsByDay).forEach(([key, count]: [string, any]) => {
        const hour = parseInt((key.split('T')[1] || '0'), 10);
        const label = `${String(hour).padStart(2, '0')}:00`;
        hourMap[label] = (hourMap[label] || 0) + count;
      });
      return Array.from({ length: 24 }, (_, i) => {
        const label = `${String(i).padStart(2, '0')}:00`;
        return { date: label, sessions: hourMap[label] || 0 };
      });
    }
    return Object.entries(analytics.sessionsByDay).map(([day, count]: [string, any]) => {
      let label = day;
      try {
        const date = parseISO(day);
        label = days <= 7 ? format(date, 'EEE d') : format(date, 'MMM d');
      } catch { /* keep raw label */ }
      return { date: label, sessions: count };
    });
  }, [analytics?.sessionsByDay]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 m-0">Analytics</h1>
          <p className="text-sm text-slate-500 mt-1">Events, sessions, and engagement · {dateLabel}</p>
        </div>
        <button
          onClick={handleExport}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-200 bg-white text-gray-700 text-xs font-medium hover:bg-slate-50 cursor-pointer transition-colors"
        >
          <Download size={14} />
          Export CSV
        </button>
      </div>

      {user?.role === 'superadmin' && !selectedTenantId && (
        <div className="p-6 bg-red-50 rounded-xl border border-red-300 text-red-900">
          Please select a tenant from the dropdown above to view data.
        </div>
      )}

      {isLoading && !analytics ? (
        <div className="p-6 text-center text-slate-500">
          <div className="loading-spinner inline-block w-6 h-6 border-2 border-slate-200 border-t-blue-700 rounded-full animate-spin" />
          <p className="mt-3">Loading analytics...</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="dashboard-kpi bg-gradient-to-br from-sky-50 to-blue-50 p-4 rounded-lg">
              <div className="text-xs font-medium text-sky-700">Total Events</div>
              <div className="text-2xl font-bold text-sky-700 mt-2">{analytics?.totalEvents || 0}</div>
            </div>
            <div className="dashboard-kpi bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-lg">
              <div className="text-xs font-medium text-green-700">Unique Users</div>
              <div className="text-2xl font-bold text-green-700 mt-2">{analytics?.uniqueUsers || 0}</div>
            </div>
            <div className="dashboard-kpi bg-gradient-to-br from-purple-50 to-violet-50 p-4 rounded-lg">
              <div className="text-xs font-medium text-purple-700">Unique Sessions</div>
              <div className="text-2xl font-bold text-purple-700 mt-2">{analytics?.uniqueSessions || 0}</div>
            </div>
            <div className="dashboard-kpi bg-gradient-to-br from-amber-50 to-yellow-50 p-4 rounded-lg">
              <div className="text-xs font-medium text-amber-700">NFC Scans</div>
              <div className="text-2xl font-bold text-amber-700 mt-2">{analytics?.nfcScans || 0}</div>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Event Type Distribution */}
            <div className="dashboard-card p-6">
              <h2 className="text-base font-semibold text-slate-900 m-0 mb-4">Event Type Distribution</h2>
              {eventTypeData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={eventTypeData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {eventTypeData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-72 flex items-center justify-center bg-slate-100 rounded-lg">
                  <span className="text-slate-400">No event data available</span>
                </div>
              )}
            </div>

            {/* Sessions Over Time */}
            <div className="dashboard-card p-6">
              <h2 className="text-base font-semibold text-slate-900 m-0 mb-4">Sessions Over Time</h2>
              {sessionData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={sessionData}>
                    <defs>
                      <linearGradient id="colorSessions" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#94a3b8' }} />
                    <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} />
                    <Tooltip />
                    <Area
                      type="monotone"
                      dataKey="sessions"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorSessions)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-72 flex items-center justify-center bg-slate-100 rounded-lg">
                  <span className="text-slate-400">No session data available</span>
                </div>
              )}
            </div>

            {/* QR Scans */}
            <div className="dashboard-card p-6 md:col-span-2">
              <h2 className="text-base font-semibold text-slate-900 m-0 mb-4">Scan Methods Distribution</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart
                      data={[
                        { name: 'NFC', value: analytics?.nfcScans || 0 },
                        { name: 'QR', value: analytics?.qrScans || 0 },
                      ]}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" fill="#3b82f6" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-4 flex flex-col justify-center">
                  <div>
                    <div className="text-sm text-gray-600">NFC Scans</div>
                    <div className="text-3xl font-bold text-blue-600">{analytics?.nfcScans || 0}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">QR Scans</div>
                    <div className="text-3xl font-bold text-purple-600">{analytics?.qrScans || 0}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
